import React, { useEffect, useState } from 'react';
import { useLayout } from '../../../hooks/screen/useLayout';
import { MainHead } from '../../../components/common/head/MainHead';
import styled from 'styled-components';
import { CropLiveTradeData } from './components/CropLiveTradeData';
import { WholesaleMarketSelectButton } from '../components/WholesaleMarketSelectButton';
import { IWholesaleMarket, wholesaleMarketConstants } from '../../../constants/cropTrends/wholesaleMarket.constant';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { pageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { useGetCropPriceSummary } from '../../../hooks/api/crop-trends/useGetCropPriceSummary.query';
import { AnimateLoading } from '../../../components/common/loading/AnimateLoading';
import { CropPriceGraph } from './components/CropPriceGraph';
import { CropPriceSummary } from './components/CropPriceSummary';
import { useGetCropDailyGraph } from '../../../hooks/api/crop-trends/useGetCropDailyGraph.query';
import { ChooseWholesaleMarketModal } from '../components/ChooseWholesaleMarketModal';
import { PopupConfirmModal } from '../../../components/common/modal/popup/PopupConfirmModal';
import { useGetDownloadAppModalHeight } from '../../../hooks/modal/useGetDownloadAppModalHeight';

export const CropTrendsDetailPage = () => {
  const [searchParam, setSearchParam] = useSearchParams();
  const [currentMarket, setCurrentMarket] = useState<IWholesaleMarket>();
  const [isCannotFavoriteCropModalShow, setIsCannotFavoriteCropModalShow] = useState<boolean>(false);
  const [isCropFavoriteModalShow, setIsCropFavoriteModalShow] = useState<boolean>(false);

  const marketCode = searchParam.get('marketCode');
  const largeCategoryCode = searchParam.get('largeCategoryCode');
  const middleCategoryCode = searchParam.get('middleCategoryCode');
  const smallCategoryCode = searchParam.get('smallCategoryCode');

  const MAIN_HEAD_HEIGHT = 48;
  const downloadAppModalHeight = useGetDownloadAppModalHeight();

  const isPageForMiddleCategoryCrop = !!middleCategoryCode && !smallCategoryCode;

  const { data: cropPriceSummary, isLoading: isLoadingCropPriceSummary } = useGetCropPriceSummary({
    searchParam: {
      wholesaleMarketCode: marketCode === 'all' ? '' : marketCode!,
      largeCategoryCode: largeCategoryCode ?? '',
      middleCategoryCode: middleCategoryCode ?? '',
      smallCategoryCode: smallCategoryCode ?? '',
    },
    options: {
      enabled: Boolean(marketCode && largeCategoryCode && middleCategoryCode),
    },
  });

  const { data: cropDailyGraph, isLoading: isLoadingCropDailyGraph } = useGetCropDailyGraph({
    searchParam: {
      wholesaleMarketCode: marketCode === 'all' ? '' : marketCode!,
      displayDataType: 'dailyAveragePrice',
      largeCategoryCode: largeCategoryCode ?? '',
      middleCategoryCode: middleCategoryCode ?? '',
      smallCategoryCode: smallCategoryCode ?? '',
    },
    options: {
      enabled: Boolean(marketCode && largeCategoryCode && middleCategoryCode),
    },
  });

  const handleMarketChange = (selectedMarket: IWholesaleMarket) => {
    const newParams = new URLSearchParams(searchParam);
    newParams.set('marketCode', selectedMarket.code);
    setSearchParam(newParams, { replace: true });

    setCurrentMarket(selectedMarket);
  };

  useLayout({
    fixedHeader: (
      <MainHead
        backButton={{ isShow: true }}
        headerTitle={`${cropPriceSummary?.categoryName ?? ''}${isPageForMiddleCategoryCrop ? ' 전체' : ''}`}
        // TODO : 즐겨찾기 관련 코드 삭제 https://gint-team.atlassian.net/browse/PVM-3459
        // favoriteButton={{
        //   clickHandler: () => {
        //     if (isPageForMiddleCategoryCrop) {
        //       setIsCannotFavoriteCropModalShow(true);
        //       return;
        //     }
        //     setIsCropFavoriteModalShow(true);
        //   },
        // }}
      />
    ),
    fixedHeaderHeight: MAIN_HEAD_HEIGHT,
    fixedFooter: <></>,
    fixedFooterHeight: 0,
    shouldShowFooter: false,
  });

  useEffect(() => {
    if (!marketCode) {
      return;
    }

    if (marketCode === 'all') {
      setCurrentMarket({ code: 'all', name: '도매시장 전체' });
      return;
    }

    const market = wholesaleMarketConstants.find((market) => market.code === marketCode);
    if (market) {
      setCurrentMarket(market);
    }
  }, [marketCode]);

  if (isLoadingCropPriceSummary || isLoadingCropDailyGraph) {
    return <AnimateLoading />;
  }

  if (currentMarket && cropPriceSummary && marketCode && largeCategoryCode && middleCategoryCode) {
    return (
      <>
        <Container>
          <SelectMarketSection top={downloadAppModalHeight + MAIN_HEAD_HEIGHT}>
            <WholesaleMarketSelectButton
              currentMarket={currentMarket}
              handleMarketChange={(selectedMarket) => handleMarketChange(selectedMarket)}
              page={'detail'}
            />
          </SelectMarketSection>
          <CropPriceSummarySection>
            <CropPriceSummary
              avgPrice={cropPriceSummary.todayTradeInfo.averagePrice}
              maxPrice={cropPriceSummary.todayTradeInfo.highestPrice}
              minPrice={cropPriceSummary.todayTradeInfo.lowestPrice}
              prevPrice={cropPriceSummary.dailyChange.previousPrice}
              changeAmount={cropPriceSummary.dailyChange.changeAmount}
              changeRate={cropPriceSummary.dailyChange.changeRate}
              totalKg={cropPriceSummary.todayTradeInfo.totalQuantity}
              totalPrice={cropPriceSummary.todayTradeInfo.totalPrice}
              tradeCalculationDate={cropPriceSummary.tradeCalculationDate}
              categoryName={cropPriceSummary.categoryName}
            >
              {cropDailyGraph && (
                <CropPriceGraph
                  labels={cropDailyGraph.label}
                  lastYearData={cropDailyGraph.lastYearData}
                  thisYearData={cropDailyGraph.thisYearData}
                />
              )}
            </CropPriceSummary>
          </CropPriceSummarySection>
          <CropLiveTradeData
            marketCode={marketCode}
            largeCategoryCode={largeCategoryCode}
            middleCategoryCode={middleCategoryCode}
            smallCategoryCode={smallCategoryCode ?? ''}
          />
        </Container>
        {/* <ChooseWholesaleMarketModal
          isShow={isCropFavoriteModalShow}
          closeModal={() => setIsCropFavoriteModalShow(false)}
          largeCategoryCode={largeCategoryCode}
          middleCategoryCode={middleCategoryCode}
          smallCategoryCode={smallCategoryCode ?? ''}
        /> */}
        <PopupConfirmModal
          isShow={isCannotFavoriteCropModalShow}
          closeModal={() => setIsCannotFavoriteCropModalShow(false)}
          title={'⭐ 관심 농산물 등록 하기'}
          description={'관심 농산물은 소분류(품종)만 가능해요!\n리스트로 돌아가서\n소분류 농산물을 등록 해보세요!'}
          buttonArray={[
            {
              text: '확인',
              onClick: () => setIsCannotFavoriteCropModalShow(false),
              color: 'blue',
            },
          ]}
        />
      </>
    );
  }

  return null;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 0 0;
  align-self: stretch;
`;

const SelectMarketSection = styled.div<{ top: number }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;

  padding: 16px;

  position: sticky;
  top: ${(props) => `${props.top}px`};
  background-color: #fff;

  z-index: 10;
`;

const CropPriceSummarySection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;

  padding: 16px 0px 32px 0px;
`;
